'use client';

import AppFooter from '@/components/layouts/AppFooter';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from '@/components/ui/pagination';
import { Button } from '@/components/ui/button';
import { ChevronsLeft } from 'lucide-react';
import { ChevronLeft } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { ChevronsRight } from 'lucide-react';
import Link from 'next/link';
import { LogoRegister, LogoSlogan, LogoText } from '@/components/common';
import CommonNav from '@/components/layouts/CommonNav';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import Image from 'next/image';
import { Calendar } from 'lucide-react';
import { Clock } from 'lucide-react';
import { stripHtml } from '@/lib/strip-html';
import { ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { View } from 'lucide-react';
import { Eye } from 'lucide-react';
import { LogoLinks } from '@/components/common/smartguy-logo';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import debounce from 'lodash.debounce';
import VideosByCategory from './VideosByCategory';
import { ArrowUp, ArrowDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const SORT_BY_OPTIONS = new Set(['title', 'createdAt', 'views']);
const ORDER_OPTIONS = new Set(['asc', 'desc']);

export default function Videos({
  categories,
  subCategories,
  data,
  categorySlug,
  subCategorySlug,
  categoryVideos,
  initialSearchParams = {},
}) {
  const router = useRouter();

  const [openDropdown, setOpenDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedCategorySlug = useMemo(() => categorySlug, [categorySlug]);

  const activeSortBy = SORT_BY_OPTIONS.has(initialSearchParams.sortBy)
    ? initialSearchParams.sortBy
    : 'views';
  const activeOrder = ORDER_OPTIONS.has(initialSearchParams.order)
    ? initialSearchParams.order
    : 'desc';

  const isDefaultSort = activeSortBy === 'views' && activeOrder === 'desc';

  const buildLibraryHref = (page) => {
    const params = new URLSearchParams();
    if (page > 1) {
      params.set('page', String(page));
    }
    // Only expose non-default sort in crawlable pagination links
    if (!isDefaultSort) {
      params.set('sortBy', activeSortBy);
      params.set('order', activeOrder);
    }
    const qs = params.toString();
    return qs ? `/videos?${qs}` : '/videos';
  };

  const updateSort = (nextSortBy, nextOrder) => {
    const params = new URLSearchParams();
    // Default sort uses clean /videos URL (no sort query params)
    if (!(nextSortBy === 'views' && nextOrder === 'desc')) {
      params.set('sortBy', nextSortBy);
      params.set('order', nextOrder);
    }
    const qs = params.toString();
    router.push(qs ? `/videos?${qs}` : '/videos');
  };

  const formatViews = (number) => {
    if (number < 1000) return number.toString();
    if (number < 1_000_000) return (number / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    if (number < 1_000_000_000) return (number / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    return (number / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;

    const months = Math.floor(days / 30);
    if (months < 12) {
      if (months === 1) return '1 month ago';
      return `${months} months ago`;
    }

    // For dates older than 1 year, you can choose either format:
    // Option 1: Return "X years ago"
    const years = Math.floor(months / 12);
    if (years === 1) return '1 year ago';
    return `${years} years ago`;

    // Option 2: Return formatted date string for very old dates
    // return date.toLocaleDateString();
  };

  const searchBlogs = async (query) => {
    // &${categoryId ? `blogCategory=${categoryId}&` : ''
    const url = `${process.env.NEXT_PUBLIC_API_URL}/videos/library?query=${query}&page=1`;

    try {
      const res = await fetch(url);

      if (!res.ok) {
        // return null
      } else {
        const result = await res.json();
        return result;
      }
    } catch (error) {
      return [];
      return null;
    }
  };

  const MIN_SEARCH_LENGTH = 3;

  const debouncedSearchVideos = useCallback(
    debounce(async (query) => {
      if (!query.trim() || query.trim().length < MIN_SEARCH_LENGTH) {
        setResult([]);
        setSearching(false);
        return;
      }

      try {
        const data = await searchBlogs(query);
        setResult(Array.isArray(data?.data) ? data?.data : []);
      } catch {
        setResult([]);
      } finally {
        setSearching(false);
      }
    }, 500),
    []
  );

  const handleInputChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.length >= MIN_SEARCH_LENGTH) {
      setSearching(true);
      setOpenDropdown(true);
      debouncedSearchVideos(query);
    } else if (query.length >= 1) {
      setResult([]);
      setSearching(false);
      setOpenDropdown(true);
    } else {
      setResult([]);
      setSearching(false);
      setOpenDropdown(false);
    }
  };

  const handleOptionSelect = (link) => {
    router.push(link);
  };

  return (
    <div className="h-full w-full">
      <div className="space-y-4">
        <div className="flex flex-1 flex-col items-center justify-center py-4">
          <Link href="/" className="flex flex-col items-center justify-center">
            <div className="flex items-center">
              <LogoText className="text-3xl" />
              <LogoRegister />
            </div>
            <LogoSlogan className="me-4 text-center text-[.7rem]" />
          </Link>

          <LogoLinks />
        </div>
        <div>
          <CommonNav />
        </div>
      </div>

      <div className="container mx-auto space-y-4">
        <div className="mx-auto w-full max-w-xs sm:max-w-md">
          <div className="relative mt-3 mb-6 w-full">
            <div className="group relative flex items-center transition-all duration-300 hover:scale-[1.01]">
              <Search
                className="text-primary absolute top-1/2 left-4 z-20 h-4 w-4 -translate-y-1/2 transform transition-colors duration-300"
                aria-hidden="true"
              />
              <Input
                type="text"
                placeholder={'Search Videos topics…'}
                className="focus-visible:ring-dark-blue z-10 w-full max-w-lg rounded-full py-6 pr-12 pl-11 text-lg focus-visible:ring-1"
                onChange={handleInputChange}
              />
            </div>

            <Popover open={openDropdown} onOpenChange={setOpenDropdown}>
              <PopoverTrigger asChild>
                <div />
              </PopoverTrigger>

              <PopoverContent
                align="start"
                sideOffset={8}
                className="mx-auto w-xs rounded-sm border-0 bg-white p-0 shadow-lg sm:w-[420px]"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <ScrollArea
                  className={`${result.length <= 5 ? 'h-max' : 'h-[400px] sm:h-[400px]'}`}
                >
                  <>
                    {searchQuery.length >= MIN_SEARCH_LENGTH &&
                      !searching &&
                      result.length > 0 &&
                      result.map((blog, index) => (
                        <div
                          key={index}
                          className="text-primary flex cursor-pointer justify-start gap-2 px-4 py-2 hover:bg-gray-200"
                          onClick={() => handleOptionSelect(`/videos/${blog?.seoSlug}`)}
                        >
                          {blog.thumbnailUrl ? (
                            <div className="relative h-[80px] w-[120px] flex-shrink-0 overflow-hidden rounded-[4px]">
                              <Image
                                src={`${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/${blog.thumbnailUrl}`}
                                width={130}
                                height={80}
                                alt=""
                                priority="lazy"
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="relative h-[80px] w-[120px] flex-shrink-0 overflow-hidden rounded-[4px] bg-gray-200"></div>
                          )}

                          <div className="flex flex-1 flex-col gap-1">
                            <div
                              className="line-clamp-2 text-[.9rem] font-semibold"
                              style={{ lineHeight: '18px' }}
                            >
                              {blog.title}
                            </div>
                            <div className="text-[.8rem] italic">{blog?.videoCategory?.title}</div>
                            <div className="text-[.65rem]">
                              <span className="me-2 text-blue-800">
                                {formatViews(blog.views)} views.{' '}
                              </span>
                              <span>{formatTime(blog.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      ))}

                    {searchQuery.length >= 1 &&
                      searchQuery.length < MIN_SEARCH_LENGTH &&
                      !searching && (
                        <p className="text-muted-foreground px-4 py-2 text-sm">
                          Type at least 3 characters to search.
                        </p>
                      )}

                    {searchQuery.length >= MIN_SEARCH_LENGTH &&
                      !searching &&
                      result.length === 0 && (
                        <p className="px-4 py-2">
                          No videos found. Try searching with different keywords.
                        </p>
                      )}

                    {searchQuery.length >= MIN_SEARCH_LENGTH && searching && (
                      <div className="space-y-3 px-4 py-2">
                        {[...Array(4)].map((_, i) => (
                          <div key={i} className="flex gap-3">
                            <div className="h-20 w-[130px] animate-pulse rounded-sm bg-gray-200" />

                            {/* Text skeleton */}
                            <div className="flex-1 space-y-2">
                              <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                              <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                              <div className="h-3 w-1/2 animate-pulse rounded bg-gray-200" />
                              <div className="mt-2 h-2 w-1/3 animate-pulse rounded bg-gray-200" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <VideosByCategory
          categories={categories}
          subCategories={subCategories}
          categorySlug={categorySlug}
          subCategorySlug={subCategorySlug}
          categoryVideos={categoryVideos}
        />

        {!selectedCategorySlug && (
          <>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <span className="text-muted-foreground text-sm font-medium">Sort</span>

              <div className="border-dark-blue/15 flex items-center overflow-hidden rounded-full border bg-white shadow-sm">
                <Select
                  value={activeSortBy}
                  onValueChange={(value) => updateSort(value, activeOrder)}
                >
                  <SelectTrigger className="text-dark-blue [&>svg]:text-dark-blue/60 h-9 w-[130px] cursor-pointer rounded-none border-0 bg-transparent px-4 shadow-none focus:ring-0 focus:ring-offset-0">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent align="end">
                    <SelectItem value="title">Title</SelectItem>
                    <SelectItem value="createdAt">Date</SelectItem>
                    <SelectItem value="views">Views</SelectItem>
                  </SelectContent>
                </Select>

                {/* <div className="bg-dark-blue/10 h-5 w-px shrink-0" aria-hidden="true" />

                <div className="flex items-center p-0.5" role="group" aria-label="Sort order">
                  <button
                    type="button"
                    aria-label="Sort ascending"
                    aria-pressed={activeOrder === 'asc'}
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                      activeOrder === 'asc'
                        ? 'bg-dark-blue text-white shadow-sm'
                        : 'text-dark-blue/50 hover:bg-periwinkle hover:text-dark-blue'
                    )}
                    onClick={() => updateSort(activeSortBy, 'asc')}
                  >
                    <ArrowUp className="h-4 w-4" strokeWidth={2.25} />
                  </button>
                  <button
                    type="button"
                    aria-label="Sort descending"
                    aria-pressed={activeOrder === 'desc'}
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                      activeOrder === 'desc'
                        ? 'bg-dark-blue text-white shadow-sm'
                        : 'text-dark-blue/50 hover:bg-periwinkle hover:text-dark-blue'
                    )}
                    onClick={() => updateSort(activeSortBy, 'desc')}
                  >
                    <ArrowDown className="h-4 w-4" strokeWidth={2.25} />
                  </button>
                </div> */}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {data &&
                data.data.length > 0 &&
                data.data.map((blog, index) => (
                  <Card
                    key={index}
                    className="group col-span-1 w-full gap-1 p-0 pb-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  >
                    <CardHeader className="p-0">
                      <Link href={`/videos/${blog.seoSlug}`}>
                        <div className="relative h-[220px] w-full overflow-hidden rounded-t-lg">
                          <Image
                            src={`${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/${blog.thumbnailUrl}`}
                            alt={blog.title}
                            fill
                            className="object-cover"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          />
                        </div>
                      </Link>
                    </CardHeader>

                    <CardContent>
                      <div className="text-muted-foreground mb-3 flex w-full items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {blog?.createdAt && format(new Date(blog.createdAt), 'dd MMM yyyy')}
                          </span>
                        </div>

                        <div className="flex items-center gap-1">
                          <Eye />
                          <span>{formatViews(blog?.views)}</span>
                        </div>
                      </div>

                      <CardTitle className="group-hover:text-primary line-clamp-2 text-xl font-bold transition-colors">
                        <Link href={`/videos/${blog.seoSlug}`}>{blog.title}</Link>
                      </CardTitle>

                      <CardDescription className="mt-2 line-clamp-3">
                        {stripHtml(blog.description)}
                      </CardDescription>
                    </CardContent>

                    <CardFooter className="flex items-center justify-end pt-2">
                      <Link
                        href={`/videos/${blog.seoSlug}`}
                        className="hover:text-primary flex items-center gap-1 text-sm font-semibold"
                      >
                        Read more
                        <ArrowRight className="relative top-[1px] h-4 w-4" />
                      </Link>
                    </CardFooter>
                  </Card>
                ))}
            </div>

            <div className="mt-5 block">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <Button
                      variant="ghost"
                      className="flex w-auto px-2 disabled:cursor-not-allowed"
                      disabled={data.meta.firstPage === data.meta.page}
                    >
                      <Link
                        href={buildLibraryHref(data.meta.firstPage)}
                        className="text-md flex items-center gap-1 text-gray-600"
                      >
                        <ChevronsLeft /> First
                      </Link>
                    </Button>
                  </PaginationItem>

                  <PaginationItem>
                    <Button
                      variant="ghost"
                      className="flex w-auto px-2 disabled:cursor-not-allowed"
                      disabled={!data.meta.hasPreviousPage}
                    >
                      <Link
                        href={buildLibraryHref(data.meta.page - 1)}
                        className="text-md flex items-center gap-1 text-gray-600"
                      >
                        <ChevronLeft /> Previous
                      </Link>
                    </Button>
                  </PaginationItem>

                  <PaginationItem>
                    <PaginationLink href="#" isActive>
                      {data.meta.page}
                    </PaginationLink>
                  </PaginationItem>

                  <PaginationItem>
                    <Button
                      variant="ghost"
                      className="flex w-auto px-2 disabled:cursor-not-allowed"
                      disabled={!data.meta.hasNextPage}
                    >
                      <Link
                        href={buildLibraryHref(data.meta.page + 1)}
                        className="text-md flex items-center gap-1 text-gray-600"
                      >
                        Next <ChevronRight />
                      </Link>
                    </Button>
                  </PaginationItem>

                  <PaginationItem>
                    <Button
                      variant="ghost"
                      className="flex w-auto px-2 disabled:cursor-not-allowed"
                      disabled={data.meta.lastPage === data.meta.page}
                    >
                      <Link
                        href={buildLibraryHref(data.meta.lastPage)}
                        className="text-md flex items-center gap-1 text-gray-600"
                      >
                        Last <ChevronsRight />
                      </Link>
                    </Button>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </>
        )}

        <AppFooter />
      </div>
    </div>
  );
}
